import React, { ElementType } from 'react';
import { FontWeight } from '../../theme/design-tokens';

// Define the semantic variants for the text component
export type TextVariant =
    | 'h1' | 'h2' | 'h3' | 'h4'
    | 'body-lg' | 'body-base' | 'body-sm' | 'body-xs'
    | 'label' | 'code';

// Use AllHTMLAttributes to support props like htmlFor (label), href (a), etc. for polymorphism
// Omit 'as' to avoid conflict with our polymorphic 'as' prop
interface TextProps extends Omit<React.AllHTMLAttributes<HTMLElement>, 'as'> {
    /** The semantic variant of the text */
    variant?: TextVariant;
    /** The HTML element to render */
    as?: ElementType;
    /** Override font weight (constrained to tokens) */
    weight?: FontWeight;
    /** Tailwind color class (e.g., 'text-neutral-900', 'text-theme-text-primary') */
    color?: string;
    /** Text alignment */
    align?: 'left' | 'center' | 'right' | 'justify';
    /** Truncate text with ellipsis */
    truncate?: boolean;
    children: React.ReactNode;
}

/**
 * Maps variants to their default element and classes.
 * This ensures consistency while allowing flexibility.
 */
const VARIANT_MAP: Record<TextVariant, { as: ElementType; classes: string }> = {
    'h1': { as: 'h1', classes: 'text-4xl md:text-5xl font-extrabold tracking-tight leading-tight' },
    'h2': { as: 'h2', classes: 'text-3xl md:text-4xl font-bold tracking-tight leading-snug' },
    'h3': { as: 'h3', classes: 'text-2xl font-bold tracking-normal leading-snug' },
    'h4': { as: 'h4', classes: 'text-xl font-semibold tracking-normal leading-snug' },
    'body-lg': { as: 'p', classes: 'text-lg leading-relaxed' },
    'body-base': { as: 'p', classes: 'text-base leading-normal' },
    'body-sm': { as: 'p', classes: 'text-sm leading-normal' },
    'body-xs': { as: 'span', classes: 'text-xs leading-none' },
    'label': { as: 'label', classes: 'text-sm font-medium text-theme-text-secondary' },
    'code': { as: 'code', classes: 'font-mono text-sm bg-neutral-100 dark:bg-neutral-800 rounded px-1' },
};

/**
 * Enterprise Text Component
 * 
 * Enforces design tokens and consistent typography across the application.
 * Supports polymorphism via the `as` prop.
 * 
 * @example
 * <Text variant="h1" color="text-primary-600">Hello World</Text>
 * <Text variant="body-sm" as="span" weight="bold">Bold Span</Text>
 */
export const Text: React.FC<TextProps> = ({
    variant = 'body-base',
    as,
    weight,
    color,
    align,
    truncate,
    className = '',
    children,
    ...props
}) => {
    const { as: defaultAs, classes: defaultClasses } = VARIANT_MAP[variant];
    const Component = as || defaultAs;

    // Construct class names dynamically based on props
    const combinedClasses = [
        defaultClasses,
        weight ? `font-${weight}` : '',
        color || '', // Allow inheritance from container (e.g., Sidebar) if no explicit color provided
        align ? `text-${align}` : '',
        truncate ? 'truncate' : '',
        className
    ].filter(Boolean).join(' ');

    return (
        <Component className={combinedClasses} {...props}>
            {children}
        </Component>
    );
};

// Export alias for consistency across the codebase
export const Typography = Text;

export default Text;
